import React from 'react';
import {
  TrendingUp, Target, Handshake, Settings, FileCheck, Users,
  Shield, Eye, Award, Heart, User as UserIcon, Building2,
} from 'lucide-react';

const FOUNDERS = [
  {
    name: 'Sounav Mandal',
    role: 'Founder & Director',
    bio: 'Sounav leads strategy, business development, and investor relations at AcreMitra. With a strong foundation in real estate and a deep understanding of market dynamics, he is passionate about creating high-growth opportunities and long-term value for our clients and partners.',
    badges: [
      { icon: TrendingUp, label: 'Real Estate Strategist' },
      { icon: Target,     label: 'Growth Visionary' },
      { icon: Handshake,  label: 'Partnership Driven' },
    ],
  },
  {
    name: 'Lisa Sen',
    role: 'Co-Founder & Director',
    bio: 'Lisa oversees operations, legal due diligence, and customer experience at AcreMitra. Her attention to detail, integrity, and customer-first approach ensure transparency, trust, and seamless execution in every project.',
    badges: [
      { icon: Settings,  label: 'Operations Leader' },
      { icon: FileCheck, label: 'Legal & Title Expertise' },
      { icon: Users,     label: 'Customer Centric' },
    ],
  },
];

const VISION_PILLARS = [
  { icon: Shield, label: 'Trust' },
  { icon: Eye,    label: 'Transparency' },
  { icon: Award,  label: 'Excellence' },
  { icon: Heart,  label: 'Commitment' },
];

export const Founders = () => {
  return (
    <section
      id="founders"
      className="am-reveal py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden"
      data-testid="founders-section"
    >
      {/* faint India map silhouette as decoration */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(4,120,87,0.35) 0, transparent 30%), radial-gradient(circle at 80% 70%, rgba(245,158,11,0.25) 0, transparent 35%)',
        }}
      />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="block w-8 h-px bg-emerald-700/40" />
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-emerald-800">
              Founders
            </span>
            <span className="block w-8 h-px bg-emerald-700/40" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            Meet the Founders of{' '}
            <span className="text-emerald-700">Acre</span>
            <span className="text-amber-500">Mitra</span>
          </h2>
          <p className="text-slate-500 text-base sm:text-lg mt-3">
            Visionaries. Builders. Partners in your growth.
          </p>
        </div>

        {/* Two-column layout: photo + cards */}
        <div className="grid lg:grid-cols-5 gap-8 items-stretch">
          {/* LEFT — founder photo card */}
          <div className="lg:col-span-2">
            <div
              className="relative h-full rounded-2xl overflow-hidden shadow-2xl group"
              data-testid="founders-photo-card"
            >
              {/* Soft emerald gradient backdrop */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900" />

              {/* Animated radial glow */}
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  background:
                    'radial-gradient(circle at 50% 35%, rgba(252,211,77,0.35), transparent 55%), radial-gradient(circle at 50% 85%, rgba(16,185,129,0.4), transparent 60%)',
                }}
              />

              {/* Faint world-map dot pattern */}
              <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                  backgroundSize: '18px 18px',
                }}
              />

              {/* Photo — natural aspect, centred */}
              <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-auto lg:h-full flex items-end justify-center px-4 pt-8 lg:pt-12">
                <img
                  src="/founders.jpg"
                  alt="Sounav Mandal and Lisa Sen — AcreMitra Founders"
                  className="relative z-10 w-full max-w-md object-contain drop-shadow-[0_25px_60px_rgba(0,0,0,0.55)]"
                  data-testid="founders-photo"
                />
              </div>

              {/* Top-left badge: EST. 2026 */}
              <div className="absolute top-5 left-5 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/95 text-emerald-950 text-[10px] font-extrabold tracking-[0.2em] uppercase shadow-lg">
                <Award size={11} strokeWidth={2.8} /> Est. 2026
              </div>

              {/* Top-right badge: Founders */}
              <div className="absolute top-5 right-5 z-20 px-3 py-1.5 rounded-full bg-white/12 backdrop-blur border border-white/25 text-white text-[10px] font-bold tracking-[0.25em] uppercase">
                Founders
              </div>

              {/* Decorative corner rays — top-left */}
              <svg
                className="absolute -top-6 -left-6 z-0 opacity-80"
                width="180" height="180" viewBox="0 0 180 180"
              >
                <defs>
                  <linearGradient id="goldFade1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fcd34d" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#fcd34d" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M -10 90 Q 40 50 90 -10" stroke="url(#goldFade1)" strokeWidth="2" fill="none" />
                <path d="M -10 110 Q 50 70 110 -10" stroke="url(#goldFade1)" strokeWidth="1.5" fill="none" />
                <path d="M -10 130 Q 60 90 130 -10" stroke="url(#goldFade1)" strokeWidth="1" fill="none" />
              </svg>

              {/* Decorative corner rays — bottom-right */}
              <svg
                className="absolute -bottom-6 -right-6 z-0 opacity-80 rotate-180"
                width="180" height="180" viewBox="0 0 180 180"
              >
                <path d="M -10 90 Q 40 50 90 -10" stroke="url(#goldFade1)" strokeWidth="2" fill="none" />
                <path d="M -10 110 Q 50 70 110 -10" stroke="url(#goldFade1)" strokeWidth="1.5" fill="none" />
                <path d="M -10 130 Q 60 90 130 -10" stroke="url(#goldFade1)" strokeWidth="1" fill="none" />
              </svg>

              {/* Floating mini-cards (stats) */}
              <div className="hidden sm:flex absolute bottom-24 left-6 z-20 items-center gap-2 px-3 py-2 rounded-xl bg-white/15 backdrop-blur-md border border-white/25 shadow-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-500/30 flex items-center justify-center">
                  <Handshake size={14} className="text-emerald-100" />
                </div>
                <div>
                  <div className="text-white font-extrabold text-sm leading-none">2</div>
                  <div className="text-emerald-100/80 text-[9px] uppercase tracking-wider">Co-founders</div>
                </div>
              </div>

              <div className="hidden sm:flex absolute bottom-24 right-6 z-20 items-center gap-2 px-3 py-2 rounded-xl bg-white/15 backdrop-blur-md border border-white/25 shadow-lg">
                <div className="w-8 h-8 rounded-full bg-amber-400/30 flex items-center justify-center">
                  <Building2 size={14} className="text-amber-200" />
                </div>
                <div>
                  <div className="text-white font-extrabold text-sm leading-none">India</div>
                  <div className="text-emerald-100/80 text-[9px] uppercase tracking-wider">Trusted partner</div>
                </div>
              </div>

              {/* Bottom name strip */}
              <div className="absolute inset-x-0 bottom-0 z-10 pt-12 pb-5 px-5 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold mb-1">
                  Leadership
                </div>
                <div className="text-white font-extrabold text-lg sm:text-xl leading-tight">
                  Sounav Mandal &amp; Lisa Sen
                </div>
                <div className="text-emerald-100/80 text-xs mt-0.5">
                  Building Acre Mitra together
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — founder cards */}
          <div className="lg:col-span-3 space-y-5">
            {FOUNDERS.map((f) => (
              <div
                key={f.name}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-emerald-300 transition-all duration-300 p-6 sm:p-7"
                data-testid={`founder-card-${f.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                    <UserIcon size={28} className="text-emerald-800" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl sm:text-3xl font-bold text-emerald-900 leading-tight">{f.name}</h3>
                    <div className="text-slate-700 font-semibold text-sm mt-1">{f.role}</div>
                    <div className="w-16 h-0.5 bg-emerald-700 mt-2" />
                  </div>
                </div>
                <p className="text-slate-600 text-sm sm:text-[15px] leading-relaxed mb-5">
                  {f.bio}
                </p>
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-100">
                  {f.badges.map((b) => {
                    const Icon = b.icon;
                    return (
                      <div key={b.label} className="text-center">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 mb-2">
                          <Icon size={18} className="text-emerald-800" strokeWidth={1.8} />
                        </div>
                        <div className="text-[11px] sm:text-xs font-semibold text-slate-700 leading-tight">
                          {b.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Shared vision strip */}
        <div className="mt-10 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 grid lg:grid-cols-5 gap-6 items-center">
          <div className="lg:col-span-2 flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-full bg-emerald-800 flex items-center justify-center">
              <Building2 size={26} className="text-amber-300" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-emerald-900 mb-1">Our Shared Vision</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                To redefine real estate in India with transparency, trust, and excellence — creating landmarks that inspire and communities that thrive.
              </p>
            </div>
          </div>
          <div className="lg:col-span-3 grid grid-cols-4 gap-3 sm:gap-4 lg:border-l lg:border-slate-200 lg:pl-8">
            {VISION_PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 mb-2">
                    <Icon size={20} className="text-emerald-800" strokeWidth={1.7} />
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-slate-800">{p.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Founders;
